/**
 * useTechAuth - Tech Session Validation & 90-Day Reauthentication
 *
 * Manages tech authentication state with:
 * - Session validation (checks localStorage for valid auth data)
 * - 90-day expiry (forces re-auth after 90 days, matching baseline useAuth)
 * - Online/offline detection
 *
 * localStorage keys used:
 * - tech_auth_expiry: ISO timestamp when session expires (set at login, 90 days out)
 * - logged_in_user, user_role, tech_id, tech_name: existing auth keys
 * - last_login: ISO timestamp of last successful login
 */

import { useState, useEffect, useCallback } from 'react';

const AUTH_EXPIRY_KEY = 'tech_auth_expiry';
const SESSION_DURATION_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

export interface TechAuthState {
  /** Whether the tech has valid session data in localStorage */
  isAuthenticated: boolean;
  /** Whether the session has expired (>90 days since login) */
  isExpired: boolean;
  /** Whether the browser is currently offline */
  isOffline: boolean;
  /** Emergency offline mode: expired session but offline, allow read-only */
  isOfflineEmergency: boolean;
  /** The tech's name from localStorage */
  techName: string;
  /** The tech's ID from localStorage */
  techId: string;
  /** Refresh the auth state (call after re-authentication) */
  refreshAuth: () => void;
}

/**
 * Check if a valid tech session exists in localStorage.
 * Requires: logged_in_user, a tech persona role ('tech' or 'guest_tech'),
 * and tech_id. 'guest_tech' (lite tech) lives on /tech with reduced access,
 * so TechAuthGuard must treat it as a valid tech session — otherwise the
 * guard rejected guest_tech right after sign-in and bounced to login.
 */
function hasValidSession(): boolean {
  const loggedInUser = localStorage.getItem('logged_in_user');
  const userRole = localStorage.getItem('user_role');
  const techId = localStorage.getItem('tech_id') || localStorage.getItem('user_id');

  const isTechPersona = userRole === 'tech' || userRole === 'guest_tech';
  return !!(loggedInUser && isTechPersona && techId);
}

/**
 * Check if the tech session has expired (beyond 90-day window).
 * If no expiry is set, falls back to checking last_login.
 * If neither exists, considers session expired.
 */
function isSessionExpired(): boolean {
  const expiry = localStorage.getItem(AUTH_EXPIRY_KEY);

  if (expiry) {
    return new Date().getTime() > new Date(expiry).getTime();
  }

  // Fallback: check last_login if no expiry key exists (backward compat)
  const lastLogin = localStorage.getItem('last_login');
  if (lastLogin) {
    const loginTime = new Date(lastLogin).getTime();
    const now = new Date().getTime();
    return now - loginTime > SESSION_DURATION_MS;
  }

  // No expiry or last_login → treat as expired
  return true;
}

/**
 * Set the auth expiry to 90 days from now.
 * Called on successful login and re-authentication.
 */
export function setAuthExpiry(): void {
  const expiry = new Date(Date.now() + SESSION_DURATION_MS).toISOString();
  localStorage.setItem(AUTH_EXPIRY_KEY, expiry);
  localStorage.setItem('last_login', new Date().toISOString());
}

/**
 * Clear the auth expiry (used on logout).
 */
export function clearAuthExpiry(): void {
  localStorage.removeItem(AUTH_EXPIRY_KEY);
}

export function useTechAuth(): TechAuthState {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [authVersion, setAuthVersion] = useState(0);

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Periodically check expiry (every 60 seconds) to catch mid-session expiry
  useEffect(() => {
    const interval = setInterval(() => {
      setAuthVersion(v => v + 1);
    }, 60_000);

    return () => clearInterval(interval);
  }, []);

  const refreshAuth = useCallback(() => {
    setAuthVersion(v => v + 1);
  }, []);

  // Compute auth state (re-evaluated when authVersion changes)
  const isAuthenticated = hasValidSession();
  const isExpired = isAuthenticated && isSessionExpired();
  const isOfflineEmergency = isExpired && isOffline;

  const techId = localStorage.getItem('tech_id') || localStorage.getItem('user_id') || '';
  const techName = localStorage.getItem('tech_name') || localStorage.getItem('user_name') || '';

  // Suppress unused variable warning - authVersion drives re-renders
  void authVersion;

  return {
    isAuthenticated,
    isExpired,
    isOffline,
    isOfflineEmergency,
    techName,
    techId,
    refreshAuth,
  };
}
